"use client";

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { Share } from "lucide-react";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { cn } from "@workspace/ui/lib/utils";

import {
  SPINE_MAX_HEIGHT,
  balancedCapacity,
  evenRows,
  faceOut,
  packShelves,
  pileUp,
  spineFor,
  type ShelfSection,
} from "@/lib/books/shelf";
import { BookInspector, preloadInspector, type Inspection } from "./book-inspector";
import { CoverArt, SpineFace, type ShelfBook } from "./book-parts";
import { newsreader } from "./fonts";
import { SceneBoundary } from "./scene/scene-boundary";
import { useShelfLook, type ShelfLook, type ThemeId } from "./shelf-look";
import { DESK_CORNER, DESK_CORNER_MIN_WIDTH, THEME_STYLES, shelfCamera, type ThemeStyle } from "./shelf-themes";
import { ShelfTuner } from "./shelf-tuner";

const BookcaseScene = dynamic(() => import("./scene/bookcase-scene"), { ssr: false });

const PLANK = 26;
const PLANK_TOP = 9;
const PLANK_EDGE = PLANK - PLANK_TOP;
const HEADROOM = 26;
const ROW_HEIGHT = SPINE_MAX_HEIGHT + HEADROOM + PLANK;
// Shelves are spaced to fill the screen, within these bounds.
const MIN_ROW_HEIGHT = SPINE_MAX_HEIGHT + 12 + PLANK;
// The sunlit case: books a little smaller on a phone, where three shelves fill
// the screen and the case scrolls sideways.
const PHONE_WIDTH = 640;
const PHONE_SCALE = 0.8;
const PHONE_SHELVES = 3;
const SUNLIT_HEADROOM = 16;
const SUNLIT_MAX_ROW_HEIGHT = Math.round(ROW_HEIGHT * 1.1);
/** Open shelf under the last plank, for the plant's trailers to hang into. */
const SUNLIT_FOOT = 72;
const BOOK_GAP = 3;
/** A book shown cover out, a large format against the spines beside it. */
const FACED_HEIGHT = 204;
const FACED_RATIO = 0.66;
const FACED_MARGIN = 5;
const ARCH_WIDTH = 86;
const ARCH_HEIGHT = 130;
const PLANT_WIDTH = 170;
const FILTERS = ["reading", "favourites"] as const;
const MAX_ROW_HEIGHT = Math.round(ROW_HEIGHT * 1.5);
// Books stand a few pixels into the plank's top face rather than on its edge.
const STANDING_DEPTH = 4;

/** Room under the last row of piles for the desk top. */
const DESK_SPACE = 150;
/** The tallest a pile gets, about eighteen paperbacks, if the screen has room. */
const PILE_HEIGHT = 600;
const PILE_GAP = 24;

const EASE = "duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none";

const NONE_FACED = new Set<string>();

// Every wrapped row of books is exactly one row tall, so repeating gradients
// draw each shelf however many rows the books wrap to.
const plankBand = (row: number) => `repeating-linear-gradient(to bottom,
  transparent 0 ${row - PLANK}px,
  black ${row - PLANK}px ${row}px)`;
// Lighting on the plank: a lit top face, a shaded front edge with a bright
// arris where the two meet, and a dark lip underneath.
const plankLight = (row: number) => `repeating-linear-gradient(to bottom,
  transparent 0 ${row - PLANK}px,
  rgb(255 226 185 / 0.22) ${row - PLANK}px,
  rgb(255 226 185 / 0.08) ${row - PLANK_EDGE - 1}px,
  rgb(255 236 205 / 0.45) ${row - PLANK_EDGE - 1}px ${row - PLANK_EDGE}px,
  rgb(0 0 0 / 0.05) ${row - PLANK_EDGE}px,
  rgb(0 0 0 / 0.3) ${row - 2}px,
  rgb(0 0 0 / 0.6) ${row - 2}px ${row}px)`;
// The back of each shelf: warm light spilling down from a strip under the
// plank above, falling off into shadow behind the books.
const backLight = (row: number) => `repeating-linear-gradient(to bottom,
  rgb(255 205 140 / 0.3) 0,
  rgb(255 190 120 / 0.1) 60px,
  rgb(0 0 0 / 0) 130px,
  rgb(0 0 0 / 0.3) ${row - PLANK}px,
  transparent ${row - PLANK}px ${row}px)`;
// Oak slats on felt behind the piles, and the desk they stand on.
const deskBand = (style: ThemeStyle) => `linear-gradient(to top, ${style.shelf} ${DESK_SPACE}px, transparent ${DESK_SPACE}px),
  repeating-linear-gradient(to right, #c49d6a 0 28px, #3b332c 28px 40px)`;
// A floating shelf under each row, for the themes without a cabinet.
const shelfBand = (row: number, style: ThemeStyle) => `repeating-linear-gradient(to bottom,
  transparent 0 ${row - PLANK_EDGE - STANDING_DEPTH}px,
  ${style.shelf} ${row - PLANK_EDGE - STANDING_DEPTH}px ${row - PLANK_EDGE - STANDING_DEPTH + style.shelfThickness}px,
  transparent ${row - PLANK_EDGE - STANDING_DEPTH + style.shelfThickness}px ${row}px)`;

/**
 * One walnut bookcase filling the page: the books being read, then every
 * finished book, newest first. Books flow along the shelves without a break; a
 * brass plaque on the shelf edge marks where each section begins. Hovering a
 * book draws it out a little; clicking it takes it down to look at.
 *
 * The HTML case lays the books out and takes input. Where WebGL is available,
 * a lit 3D scene drawn from that layout replaces its visuals once ready.
 */
export function Bookcase({ sections }: { sections: ShelfSection<ShelfBook>[] }) {
  const [look, setLook] = useShelfLook();
  return look.theme === "sunlit" ? (
    <SunlitBookcase sections={sections} look={look} setLook={setLook} />
  ) : (
    <ThemedBookcase sections={sections} look={look} setLook={setLook} />
  );
}

type BookcaseProps = {
  sections: ShelfSection<ShelfBook>[];
  look: ShelfLook;
  setLook: (look: ShelfLook) => void;
};

/**
 * The room themes kept for the tuner: one case per theme, with sections marked
 * by plaques and the shelves wrapped to the page.
 */
function ThemedBookcase({ sections, look, setLook }: BookcaseProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [sceneReady, setSceneReady] = useState(false);
  const { rowHeight, rows, width } = useShelfSpacing(container, look.theme);
  const style = THEME_STYLES[look.theme];
  const books = sections.flatMap((section) => section.books);

  // The page and its header take on the room's colours.
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty("--shelf-wall", style.wall);
    root.setProperty("--shelf-ink", style.ink);
    return () => {
      root.removeProperty("--shelf-wall");
      root.removeProperty("--shelf-ink");
    };
  }, [style]);

  const onActiveChange = useCallback((id: string, active: boolean) => {
    setActiveId((current) => (active ? id : current === id ? null : current));
  }, []);
  const onSceneReady = useCallback(() => {
    setSceneReady(true);
    preloadInspector();
  }, []);
  const inspect = useCallback(
    (book: ShelfBook, button: HTMLElement) => {
      const { left, top, width, height } = button.getBoundingClientRect();
      const camera = style.cabinet || !container ? null : shelfCamera(container, look.theme);
      setInspection({
        book,
        from: { x: left, y: top, width, height },
        lying: style.piles,
        facedThickness: null,
        camera,
        returning: false,
      });
    },
    [style.cabinet, style.piles, container, look.theme],
  );
  const putBack = useCallback(() => setInspection((current) => current && { ...current, returning: true }), []);
  const onReturned = useCallback(() => setInspection(null), []);
  const flat = cn("transition-opacity duration-500", sceneReady && "opacity-0");

  const scene = container && (
    <SceneBoundary>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 overflow-hidden opacity-0 transition-opacity duration-500",
          sceneReady && "opacity-100",
        )}
      >
        <BookcaseScene
          container={container}
          books={books}
          activeId={activeId}
          hiddenId={inspection?.book.id ?? null}
          look={look}
          plankEdge={PLANK_EDGE}
          facedIds={NONE_FACED}
          scale={1}
          onReady={onSceneReady}
        />
      </div>
    </SceneBoundary>
  );

  const shelves = style.piles ? (
    <Piles
      books={books}
      width={width}
      minHeight={rows * rowHeight}
      rowHeight={rowHeight}
      hidden={sceneReady}
      inspectedId={inspection?.book.id ?? null}
      onActiveChange={onActiveChange}
      onInspect={inspect}
    />
  ) : (
    <ul
      data-case-shelves
      data-row-height={rowHeight}
      className={cn(
        "relative z-10 flex flex-wrap content-start items-end gap-x-[3px] px-4 sm:px-7",
        style.cabinet &&
          !sceneReady &&
          "shadow-[inset_16px_0_24px_-18px_rgb(0_0_0/0.8),inset_-16px_0_24px_-18px_rgb(0_0_0/0.8)]",
      )}
      style={{ minHeight: rows * rowHeight }}
    >
      {sections.map((section, index) => (
        <Fragment key={section.key}>
          {section.books.map((book, position) => (
            <ShelvedBook
              key={book.id}
              book={book}
              rowHeight={rowHeight}
              style={style}
              plaque={position === 0 ? { label: section.label, gap: index > 0 } : undefined}
              hidden={sceneReady}
              inspected={inspection?.book.id === book.id}
              onActiveChange={onActiveChange}
              onInspect={inspect}
            />
          ))}
        </Fragment>
      ))}
    </ul>
  );

  return (
    <div
      ref={setContainer}
      className={cn("relative", style.cabinet ? "p-3 sm:p-[18px]" : "px-[3vw] pt-4 pb-10 font-sans")}
    >
      {style.cabinet && (
        <div
          aria-hidden
          className={cn("absolute inset-0 shadow-[inset_0_1px_0_rgb(255_230_200/0.25),inset_0_-2px_0_rgb(0_0_0/0.35)]", flat)}
          style={{ backgroundImage: `linear-gradient(rgb(255 220 180 / 0.06), rgb(0 0 0 / 0.12)), ${style.timber}`, backgroundSize: "auto, 1600px auto" }}
        />
      )}
      {scene}
      {style.cabinet ? (
        <div data-case-inner className="relative overflow-hidden rounded-[2px]">
          <div aria-hidden className={cn("absolute inset-0 shadow-[inset_0_0_0_1px_rgb(0_0_0/0.5)]", flat)} />
          <div
            aria-hidden
            className={cn("absolute inset-0", flat)}
            style={{
              backgroundImage: `${backLight(rowHeight)}, ${style.backing}`,
              backgroundSize: `auto, auto, 1200px auto`,
            }}
          />
          <div
            aria-hidden
            className={cn("absolute inset-0", flat)}
            style={{
              backgroundImage: `${plankLight(rowHeight)}, ${style.timber}`,
              backgroundSize: "auto, 1600px auto",
              maskImage: plankBand(rowHeight),
            }}
          />
          {shelves}
        </div>
      ) : (
        <div data-case-inner className="relative">
          <div
            aria-hidden
            className={cn("absolute inset-0", flat)}
            style={{ backgroundImage: style.piles ? deskBand(style) : shelfBand(rowHeight, style) }}
          />
          {shelves}
        </div>
      )}
      {process.env.NODE_ENV === "development" && <ShelfTuner look={look} onChange={setLook} />}
      {inspection && (
        <BookInspector inspection={inspection} look={look} onClose={putBack} onReturned={onReturned} />
      )}
    </div>
  );
}

/**
 * The bookcase in daylight: a heading on the wall, then an oak case whose
 * books stand spine out with some shown cover out, a stone bookend and a plant
 * on the last shelf. On a phone the case is wider than the screen, with three
 * shelves that scroll sideways together.
 */
function SunlitBookcase({ sections, look, setLook }: BookcaseProps) {
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [sceneReady, setSceneReady] = useState(false);
  const [filter, setFilter] = useQueryState("shelf", parseAsStringLiteral(FILTERS));
  const style = THEME_STYLES.sunlit;

  const allBooks = useMemo(() => sections.flatMap((section) => section.books), [sections]);
  const books = useMemo(() => {
    if (filter === "reading") return allBooks.filter((book) => book.shelf === "reading");
    if (filter === "favourites") return allBooks.filter((book) => book.rating === 5);
    return allBooks;
  }, [allBooks, filter]);
  const placed = useMemo(() => faceOut(books), [books]);
  const facedIds = useMemo(() => new Set(placed.filter((p) => p.faced).map((p) => p.book.id)), [placed]);
  const shelves = useSunlitShelves(scroller, placed);
  const { scale, rowHeight, rows, starts, caseWidth } = shelves;
  const firstOfLast = starts.at(-1);

  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty("--shelf-wall", style.wall);
    root.setProperty("--shelf-ink", style.ink);
    return () => {
      root.removeProperty("--shelf-wall");
      root.removeProperty("--shelf-ink");
    };
  }, [style]);

  const onActiveChange = useCallback((id: string, active: boolean) => {
    setActiveId((current) => (active ? id : current === id ? null : current));
  }, []);
  const onSceneReady = useCallback(() => {
    setSceneReady(true);
    preloadInspector();
  }, []);
  const inspect = useCallback(
    (book: ShelfBook, button: HTMLElement) => {
      const { left, top, width, height } = button.getBoundingClientRect();
      setInspection({
        book,
        from: { x: left, y: top, width, height },
        lying: false,
        facedThickness: facedIds.has(book.id) ? spineFor(book).width * scale : null,
        camera: null,
        returning: false,
      });
    },
    [facedIds, scale],
  );
  const putBack = useCallback(() => setInspection((current) => current && { ...current, returning: true }), []);
  const onReturned = useCallback(() => setInspection(null), []);
  const flat = cn("transition-opacity duration-500", sceneReady && "opacity-0");

  return (
    <>
      <ShelfHeading count={allBooks.length} filter={filter} onFilter={setFilter} />
      <div
        ref={setScroller}
        data-shelf-scroll
        className="overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div ref={setContainer} className="relative p-3 sm:p-[18px]" style={{ width: caseWidth ?? undefined }}>
          <div aria-hidden className={cn("absolute inset-0", flat)} style={{ backgroundImage: style.timber }} />
          {container && (
            <SceneBoundary>
              <div
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-0 overflow-hidden opacity-0 transition-opacity duration-500",
                  sceneReady && "opacity-100",
                )}
              >
                <BookcaseScene
                  container={container}
                  books={books}
                  activeId={activeId}
                  hiddenId={inspection?.book.id ?? null}
                  look={look}
                  plankEdge={PLANK_EDGE}
                  facedIds={facedIds}
                  scale={scale}
                  onReady={onSceneReady}
                />
              </div>
            </SceneBoundary>
          )}
          <div data-case-inner className="relative overflow-hidden">
            <div aria-hidden className={cn("absolute inset-0", flat)} style={{ backgroundImage: style.backing }} />
            <div
              aria-hidden
              className={cn("absolute inset-0", flat)}
              style={{ backgroundImage: `${plankLight(rowHeight)}, ${style.timber}`, maskImage: plankBand(rowHeight) }}
            />
            <ul
              data-case-shelves
              data-row-height={rowHeight}
              className="relative z-10 flex flex-wrap content-start items-end justify-center px-2"
              style={{ columnGap: BOOK_GAP, minHeight: rows * rowHeight + SUNLIT_FOOT }}
            >
              {placed.map(({ book, faced }, index) => (
                <Fragment key={book.id}>
                  {index > 0 && starts.includes(index) && <li aria-hidden className="h-0 basis-full" />}
                  {index === firstOfLast && (
                    <DecorSlot name="arch" rowHeight={rowHeight} scale={scale} className="ml-auto" />
                  )}
                  <SunlitBook
                    book={book}
                    faced={faced}
                    rowHeight={rowHeight}
                    scale={scale}
                    hidden={sceneReady}
                    inspected={inspection?.book.id === book.id}
                    onActiveChange={onActiveChange}
                    onInspect={inspect}
                  />
                </Fragment>
              ))}
              <DecorSlot name="plant" rowHeight={rowHeight} scale={scale} className="ml-auto" />
            </ul>
          </div>
          {process.env.NODE_ENV === "development" && <ShelfTuner look={look} onChange={setLook} />}
          {inspection && (
            <BookInspector inspection={inspection} look={look} onClose={putBack} onReturned={onReturned} />
          )}
        </div>
      </div>
    </>
  );
}

const FILTER_LABELS = [
  { value: null, label: "All books" },
  { value: "reading", label: "Reading" },
  { value: "favourites", label: "Favourites" },
] as const;

/** The title on the sunlit wall above the case, with the tree's shade falling across it. */
function ShelfHeading({
  count,
  filter,
  onFilter,
}: {
  count: number;
  filter: (typeof FILTERS)[number] | null;
  onFilter: (filter: (typeof FILTERS)[number] | null) => void;
}) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      // Backing out of the share sheet is not a failure worth reporting.
      await navigator.share({ title: "Subhan\u2019s bookshelf", url }).catch(() => undefined);
      return;
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <header
      className={`${newsreader.className} relative -mt-12 bg-[var(--shelf-wall)] bg-[length:max(100%,1100px)_auto] bg-[position:right_top] bg-no-repeat px-4 pt-[4.25rem] pb-5 [background-blend-mode:multiply] sm:px-[18px] sm:pt-20`}
      style={{ backgroundImage: "url(/bookshelf/leaf-shadow.webp)" }}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[1.875rem] leading-tight font-normal tracking-[-0.015em] sm:text-[2.5rem]">
            Subhan&rsquo;s bookshelf
          </p>
          <p className="mt-0.5 text-lg italic opacity-80">
            {count} books &middot; A world of ideas
          </p>
        </div>
        <button
          type="button"
          onClick={() => void share()}
          aria-label={copied ? "Link copied" : "Share this bookshelf"}
          className="-mr-1 rounded-full p-2 transition outline-none hover:bg-current/10 focus-visible:ring-2 focus-visible:ring-current/40"
        >
          <Share className="size-6" strokeWidth={1.5} />
        </button>
      </div>
      <div className="mt-4 flex gap-1.5">
        {FILTER_LABELS.map(({ value, label }) => (
          <button
            key={label}
            type="button"
            aria-pressed={filter === value}
            onClick={() => void onFilter(value)}
            className={cn(
              "rounded-full px-4 py-1.5 text-[1.0625rem] transition outline-none focus-visible:ring-2 focus-visible:ring-current/40",
              filter === value ? "bg-[#6b241c] text-[#fbf1e3]" : "hover:bg-current/8",
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </header>
  );
}

function facedSize(scale: number) {
  const height = Math.round(FACED_HEIGHT * scale);
  return { width: Math.round(height * FACED_RATIO), height };
}

/** One slot on a sunlit shelf, one row tall: a book, spine out or cover out. */
function SunlitBook({
  book,
  faced,
  rowHeight,
  scale,
  ...button
}: {
  book: ShelfBook;
  faced: boolean;
  rowHeight: number;
  scale: number;
} & Omit<BookButtonProps, "book" | "lying" | "faced" | "scale">) {
  const size = faced ? facedSize(scale) : undefined;
  const reading = faced && book.shelf === "reading";
  return (
    <li
      className="relative flex items-end"
      style={{
        height: rowHeight,
        paddingBottom: PLANK_EDGE + STANDING_DEPTH,
        marginInline: faced ? FACED_MARGIN : undefined,
        width: size?.width ?? Math.round(spineFor(book).width * scale),
      }}
    >
      <BookButton book={book} lying={false} faced={size} scale={scale} {...button} />
      {reading && (
        <span
          className={`${newsreader.className} pointer-events-none absolute left-1/2 z-20 -translate-x-1/2 rounded-xl bg-[#fbf5ec] px-3 py-1.5 text-[13px] leading-none whitespace-nowrap text-[#2a1c13] shadow-[0_4px_14px_rgb(60_30_10/0.28)]`}
          style={{ top: rowHeight - PLANK_EDGE - STANDING_DEPTH + 6 }}
        >
          <span aria-hidden className="absolute -top-1 left-1/2 size-2.5 -translate-x-1/2 rotate-45 bg-[#fbf5ec]" />
          <span className="relative">Currently reading</span>
        </span>
      )}
    </li>
  );
}

/** Room on the shelf for something the scene draws: the bookend or the plant. */
function DecorSlot({
  name,
  rowHeight,
  scale,
  className,
}: {
  name: "arch" | "plant";
  rowHeight: number;
  scale: number;
  className?: string;
}) {
  const width = Math.round((name === "arch" ? ARCH_WIDTH : PLANT_WIDTH) * scale);
  const height = Math.round(name === "arch" ? ARCH_HEIGHT * scale : 96 * scale);
  return (
    <li
      aria-hidden
      className={cn("relative flex items-end", className)}
      style={{ height: rowHeight, width, paddingBottom: PLANK_EDGE + STANDING_DEPTH }}
    >
      <span data-decor={name} className="block" style={{ width, height }} />
    </li>
  );
}

/**
 * How the sunlit shelves fall on this screen. The widths include each book's
 * gap, so the flex row wraps exactly where the shelves are packed. On a phone
 * the case is as wide as it takes to get the books on three shelves.
 */
function useSunlitShelves(scroller: HTMLElement | null, placed: { book: ShelfBook; faced: boolean }[]) {
  const [shelves, setShelves] = useState<{
    scale: number;
    rowHeight: number;
    rows: number;
    starts: number[];
    caseWidth: number | null;
  }>({ scale: 1, rowHeight: ROW_HEIGHT, rows: 1, starts: [], caseWidth: null });

  useEffect(() => {
    if (!scroller) return;
    const fit = () => {
      const screen = scroller.clientWidth;
      const phone = window.innerWidth < PHONE_WIDTH;
      const scale = phone ? PHONE_SCALE : 1;
      const frame = phone ? 12 : 18;
      const widths = placed.map(({ book, faced }) =>
        faced
          ? facedSize(scale).width + 2 * FACED_MARGIN + BOOK_GAP
          : Math.round(spineFor(book).width * scale) + BOOK_GAP,
      );
      const reserve = Math.round((ARCH_WIDTH + PLANT_WIDTH) * scale) + 2 * BOOK_GAP;
      // The list's own padding, and a little slack so rounding never wraps a book early.
      const inset = 2 * frame + 16 + 2;
      const capacity = phone ? balancedCapacity(widths, PHONE_SHELVES, reserve) : screen - inset;
      const starts = packShelves(widths, capacity, reserve);
      const top = scroller.getBoundingClientRect().top + window.scrollY;
      const available = document.documentElement.clientHeight - top - 2 * frame - SUNLIT_FOOT;
      const least = Math.round(SPINE_MAX_HEIGHT * scale) + SUNLIT_HEADROOM + PLANK;
      const rows = Math.max(starts.length, 1, phone ? 0 : Math.floor(available / least));
      const rowHeight = Math.min(SUNLIT_MAX_ROW_HEIGHT, Math.max(least, Math.floor(available / rows)));
      const caseWidth = phone ? Math.max(screen, capacity + inset) : null;
      setShelves({ scale, rowHeight, rows, starts, caseWidth });
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(scroller);
    window.addEventListener("resize", fit);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [scroller, placed]);

  return shelves;
}

/**
 * Spaces the shelves so a whole number of them fills the screen below the
 * case's top edge. More books than fit simply run on down the page. Also
 * reports the case's width, which piles are spread across.
 */
function useShelfSpacing(container: HTMLElement | null, theme: ThemeId) {
  const [spacing, setSpacing] = useState({ rowHeight: ROW_HEIGHT, rows: 1, width: 0 });

  useEffect(() => {
    if (!container) return;
    const fit = () => {
      const { paddingTop, paddingBottom } = getComputedStyle(container);
      const frame = parseFloat(paddingTop) + parseFloat(paddingBottom);
      const top = container.getBoundingClientRect().top + window.scrollY;
      const available = document.documentElement.clientHeight - top - frame;
      const rows = Math.max(1, Math.floor(available / MIN_ROW_HEIGHT));
      const rowHeight = Math.min(MAX_ROW_HEIGHT, Math.max(MIN_ROW_HEIGHT, Math.floor(available / rows)));
      setSpacing({ rowHeight, rows, width: container.clientWidth });
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
    // Each theme frames the shelves differently.
  }, [container, theme]);

  return spacing;
}

/**
 * The label under the first book of each year: a small brass plaque on the
 * classic case's shelf edge, or plain text hung under a floating shelf. It
 * belongs to that book's slot so the two always wrap onto a shelf together.
 */
function Plaque({ label, rowHeight, style }: { label: string; rowHeight: number; style: ThemeStyle }) {
  return (
    <h2
      className={cn("absolute left-0 z-10 whitespace-nowrap tabular-nums", style.label)}
      style={
        style.cabinet
          ? { bottom: (PLANK_EDGE - 13) / 2 }
          : { top: rowHeight - PLANK_EDGE - STANDING_DEPTH + style.shelfThickness + 12 }
      }
    >
      {label}
    </h2>
  );
}

/**
 * The books in piles on a desk, spine out, in order from the top of the first
 * pile. There are as many piles as fit across, of about the same height; only
 * piles that would grow taller than the screen allows wrap onto another row.
 * The piles keep to the left, leaving the corner of a wide desk for the lamp,
 * and the front row is always full, so the desk is never half empty.
 */
function Piles({
  books,
  width,
  minHeight,
  rowHeight,
  hidden,
  inspectedId,
  onActiveChange,
  onInspect,
}: {
  books: ShelfBook[];
  /** The case's width, which the piles are spread across. */
  width: number;
  minHeight: number;
  rowHeight: number;
  hidden: boolean;
  inspectedId: string | null;
  onActiveChange: (id: string, active: boolean) => void;
  onInspect: (book: ShelfBook, button: HTMLElement) => void;
}) {
  const corner = width >= DESK_CORNER_MIN_WIDTH ? DESK_CORNER : 0;
  const room = width - (width >= 640 ? 56 : 32) - corner;
  const across = Math.max(1, Math.floor((room + PILE_GAP) / (SPINE_MAX_HEIGHT + PILE_GAP)));
  const tallest = Math.max(200, Math.min(PILE_HEIGHT, minHeight - DESK_SPACE - 60));
  const rows = useMemo(() => {
    const thickness = (book: ShelfBook) => spineFor(book).width;
    const total = books.reduce((sum, book) => sum + thickness(book), 0);
    // Just over an even share, so the piles come out `across` in number.
    const piles = pileUp(books, thickness, Math.min(tallest, total / Math.max(1, across - 0.6)));
    // Rows are filled from the front so the desk, drawn against the last row, is never half empty.
    return evenRows(piles, across);
  }, [books, across, tallest]);

  return (
    <div
      data-case-shelves
      data-row-height={rowHeight}
      className="relative z-10 flex flex-col justify-end gap-y-24 px-4 sm:px-7"
      style={{ minHeight, paddingBottom: DESK_SPACE, paddingRight: corner + 28 }}
    >
      {rows.map((row) => (
        <ul key={row[0]?.[0]?.id} className="flex flex-nowrap items-end justify-start" style={{ columnGap: PILE_GAP }}>
          {row.map((pile) => (
            <li key={pile[0]?.id} data-pile className="relative flex flex-col items-center">
              {pile.map((book) => (
                <BookButton
                  key={book.id}
                  book={book}
                  lying
                  hidden={hidden}
                  inspected={inspectedId === book.id}
                  onActiveChange={onActiveChange}
                  onInspect={onInspect}
                />
              ))}
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}

/** A spine standing on the shelf, in a slot one row tall. */
function ShelvedBook({
  book,
  rowHeight,
  style,
  plaque,
  ...button
}: {
  book: ShelfBook;
  rowHeight: number;
  style: ThemeStyle;
  /** Set on the first book of a section; `gap` separates it from the section before. */
  plaque?: { label: string; gap: boolean };
} & Omit<BookButtonProps, "book" | "lying">) {
  const spine = spineFor(book);
  return (
    <li
      className={cn("relative flex items-end", plaque?.gap && "ml-5")}
      style={{ height: rowHeight, paddingBottom: PLANK_EDGE + STANDING_DEPTH, width: spine.width }}
    >
      {plaque && <Plaque label={plaque.label} rowHeight={rowHeight} style={style} />}
      <BookButton book={book} lying={false} {...button} />
    </li>
  );
}

type BookButtonProps = {
  book: ShelfBook;
  /** Lying on its front cover in a pile, rather than standing on its tail. */
  lying: boolean;
  /** The 3D scene is drawing this book, so only the hit area remains. */
  hidden: boolean;
  /** Taken down to look at. */
  inspected: boolean;
  /** Standing cover out, in a box this size, rather than spine out. */
  faced?: { width: number; height: number };
  /** The size of the spine on the shelf, for a phone's smaller books. */
  scale?: number;
  onActiveChange: (id: string, active: boolean) => void;
  onInspect: (book: ShelfBook, button: HTMLElement) => void;
};

/**
 * A book's spine. Hovering or focusing it draws it out a little; clicking it
 * takes it down.
 */
function BookButton({ book, lying, hidden, inspected, faced, scale = 1, onActiveChange, onInspect }: BookButtonProps) {
  const spine = spineFor(book);
  const width = Math.round(spine.width * scale);
  const height = Math.round(spine.height * scale);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const active = (hovered || focused) && !inspected;

  useEffect(() => {
    onActiveChange(book.id, active);
  }, [book.id, active, onActiveChange]);

  // A standing book tips out by its top; one in a pile slides out, its front edge lifted.
  const drawn = lying ? "translate3d(0, 0, 96px) rotateX(-7deg)" : "translate3d(0, -5px, 30px) rotateX(5deg)";
  // Piled books are never quite square to each other.
  const offset = lying ? ((spine.typeface % 11) - 5) * 2 : 0;

  return (
    <button
      type="button"
      aria-label={`${book.title} by ${book.author}`}
      aria-haspopup="dialog"
      data-book-id={book.id}
      className="relative block shrink-0 cursor-pointer outline-none [perspective:1100px] [-webkit-tap-highlight-color:transparent]"
      style={
        lying
          ? { width: spine.height, height: spine.width, transform: `translateX(${offset}px)` }
          : (faced ?? { width, height })
      }
      onPointerEnter={(event) => event.pointerType === "mouse" && setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      // Only keyboard focus draws the book out; focus returned after a click would leave it standing out.
      onFocus={(event) => setFocused(event.currentTarget.matches(":focus-visible"))}
      onBlur={() => setFocused(false)}
      onClick={(event) => onInspect(book, event.currentTarget)}
    >
      <span
        className={cn(
          "absolute inset-0 transition-transform [transform-origin:bottom] [transform-style:preserve-3d]",
          EASE,
          (hidden || inspected) && "opacity-0",
        )}
        style={{ transform: active ? drawn : "translate3d(0, 0, 0)" }}
      >
        {faced ? (
          <span className="absolute inset-0 overflow-hidden rounded-[2px] text-[15px] shadow-[0_2px_6px_rgb(0_0_0/0.4)]">
            <CoverArt book={book} spine={spine} sizes={`${faced.width}px`} />
          </span>
        ) : (
          <SpineFace
            book={book}
            spine={spine}
            width={width}
            height={height}
            // Lying on its front cover, the spine reads from left to right.
            className={lying ? "top-1/2 left-1/2" : "inset-0"}
            style={{
              boxShadow: "0 2px 2px rgb(0 0 0 / 0.5), 4px 0 10px -3px rgb(0 0 0 / 0.55)",
              ...(lying && { transform: "translate(-50%, -50%) rotate(-90deg)" }),
            }}
          />
        )}
      </span>
    </button>
  );
}
